import React, { useEffect, useState } from "react";
import axios from "axios";
import Pagination from "../../Pagination";
import MetricNavbar from "./MetricNavbar";
import * as XLSX from "xlsx";

function RecruiterRankingDashboard() {
  const [rankings, setRankings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [genericMetrics, setGenericMetrics] = useState({});
  const rowsPerPage = 6;

  useEffect(() => {
    const fetchRankings = async () => {
      const storedClient = localStorage.getItem("client");

      // Convert stored values to numbers
      const client_id = storedClient ? Number(storedClient) : null;

      // Debugging: Log retrieved values
      console.log("Fetching data with:", { client_id });

      // Ensure client_id and user_id are valid before making the request
      if (!client_id) {
        console.error("Missing client_id . Please log in again.");
        return;
      }
      try {
        const response = await axios.get(
          `${process.env.REACT_APP_API_URL}/screener/api-metrics/?client_id=${client_id}`
        );

        const data = response.data.recruiter_ranking || [];

        const mapped = data.map((r) => ({
          recruiter_id: r.recruiter,
          jds_posted: r.jds_posted || 0,
          total_closures: r.hires || 0,
          profiles_submitted: r.profiles_submitted || 0,
          score: parseFloat(
            (((r.hires || 0) / (r.profiles_submitted || 1)) * 100).toFixed(2)
          ),
        }));

        const sorted = mapped.sort((a, b) => b.score - a.score);
        setRankings(sorted);

        setGenericMetrics({
          avg_time_to_close_jd: parseFloat(
            response.data.avg_time_to_close_jd
          ).toFixed(2),
          resume_to_hire_conversion_rate: parseFloat(
            response.data.resume_to_hire_conversion_rate
          ).toFixed(2),
          shortlisted_to_hire_ratio: parseFloat(
            response.data.shortlisted_to_hire_ratio
          ).toFixed(2),
          avg_time_per_resume_hrs: response.data.avg_time_per_resume_hrs,
          jd_fill_rate: parseFloat(response.data.jd_fill_rate).toFixed(2),
          offer_to_joined_ratio: parseFloat(
            response.data.offer_to_joined_ratio
          ).toFixed(2),
          submitted_to_offer_ratio: parseFloat(
            response.data.submitted_to_offer_ratio
          ).toFixed(2),
        });
      } catch (err) {
        console.error(err);
        setError("Failed to load recruiter efficiency data.");
      } finally {
        setLoading(false);
      }
    };

    fetchRankings();
  }, []);

  const exportToExcel = () => {
    const exportData = rankings.map((item) => ({
      "Recruiter ID": item.recruiter_id,
      "JDs Posted": item.jds_posted,
      "Profiles Submitted": item.profiles_submitted,
      "Total Closures": item.total_closures,
      "Overall Performance (%)": item.score,
    }));

    // Calculate totals from all rankings
    const totalRow = {
      "Recruiter ID": "Total",
      "JDs Posted": rankings.reduce((sum, r) => sum + (r.jds_posted || 0), 0),
      "Profiles Submitted": rankings.reduce(
        (sum, r) => sum + (r.profiles_submitted || 0),
        0
      ),
      "Total Closures": rankings.reduce(
        (sum, r) => sum + (r.total_closures || 0),
        0
      ),
      "Overall Performance (%)": parseFloat(
        (
          rankings.reduce((sum, r) => sum + (r.score || 0), 0) / rankings.length
        ).toFixed(2)
      ),
    };

    // Append total row
    exportData.push(totalRow);

    const worksheet = XLSX.utils.json_to_sheet(exportData);
    worksheet["!cols"] = Object.keys(exportData[0]).map((key) => {
      const maxLength = Math.max(
        key.length,
        ...exportData.map((row) => String(row[key] || "").length)
      );
      return { wch: maxLength + 2 };
    });

    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Recruiter Ranking");
    XLSX.writeFile(workbook, "Recruiter_Efficiency_Report.xlsx");
  };

  const filteredRankings = rankings.filter(
    (r) =>
      (r.recruiter_id &&
        r.recruiter_id.toLowerCase().includes(searchQuery.toLowerCase())) ||
      (r.name && r.name.toLowerCase().includes(searchQuery.toLowerCase()))
  );

  const totals = {
    jds_posted: 0,
    profiles_submitted: 0,
    total_closures: 0,
    score: 0,
  };

  let activeRecruitersWithProfiles = 0;

  filteredRankings.forEach((r) => {
    totals.jds_posted += r.jds_posted || 0;
    totals.profiles_submitted += r.profiles_submitted || 0;
    totals.total_closures += r.total_closures || 0;

    // Only include in score average if profiles were actually submitted
    if ((r.profiles_submitted || 0) > 0) {
      totals.score += r.score || 0;
      activeRecruitersWithProfiles += 1;
    }
  });

  // Use only active recruiters for average
  totals.score =
    activeRecruitersWithProfiles > 0
      ? parseFloat((totals.score / activeRecruitersWithProfiles).toFixed(2))
      : 0;

  const indexOfLastRow = currentPage * rowsPerPage;
  const indexOfFirstRow = indexOfLastRow - rowsPerPage;
  const currentRows = filteredRankings.slice(indexOfFirstRow, indexOfLastRow);
  const totalPages = Math.ceil(filteredRankings.length / rowsPerPage);

  if (loading)
    return (
      <div
        className="container d-flex justify-content-center align-items-center"
        style={{ height: "100vh" }}
      >
        <div>
          <div
            className="spinner-border text-dark"
            role="status"
            style={{ width: "3rem", height: "3rem" }}
          >
            <span className="visually-hidden">Loading...</span>
          </div>
        </div>
        <h3 className="text-center text-dark">&nbsp;Loading data.... </h3>
      </div>
    );

  if (error) return <div className="text-danger text-center py-5">{error}</div>;

  return (
    <>
      <MetricNavbar />
      <div className="container py-4">
        <h2 className="text-center mb-4 font_header">
          Recruiter Efficiency Dashboard
        </h2>

        <div className="row">
          {[
            {
              label: "Avg Time of Closure",
              value: `${genericMetrics.avg_time_to_close_jd || 0} days`,
            },
            {
              label: "Resume to Hire %",
              value: `${genericMetrics.resume_to_hire_conversion_rate || 0}`,
            },
            {
              label: "Shortlisted to Hire Ratio",
              value: genericMetrics.shortlisted_to_hire_ratio || 0,
            },
            {
              label: "Avg Time per Resume (hrs)",
              value: `${(genericMetrics.avg_time_per_resume_hrs || 0).toFixed(
                2
              )} hrs`,
            },
            {
              label: "JD Fill Rate",
              value: `${genericMetrics.jd_fill_rate || 0}`,
            },
            {
              label: "Offer to Joined Ratio",
              value: `${genericMetrics.offer_to_joined_ratio || 0}`,
            },
            {
              label: "Submitted to Offer Ratio",
              value: `${genericMetrics.submitted_to_offer_ratio || 0}`,
            },
          ].map((item, index) => (
            <div className="col-md-3 mb-4" key={index}>
              <div className="custom-card text-center text-white animate__animated animate__fadeInUp">
                <div className="custom-card-header fw-bold">{item.label}</div>
                <div className="custom-card-body">
                  <span className="custom-card-link">{item.value}</span>
                </div>
              </div>
            </div>
          ))}
        </div>

        <div className="row mb-4">
          <div className="col-md-3 text-start">
            <label htmlFor="searchInput" className="form-label fw-semibold">
              Search by Recruiter ID:
            </label>
            <input
              type="text"
              id="searchInput"
              className="form-control border-dark"
              placeholder="Search by recruiter ID or name..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
          </div>
          <div className="col-md text-lg-end mt-4">
            <button className="btn btn-outline-info" onClick={exportToExcel}>
              Download
            </button>
          </div>
        </div>

        <div className="table-responsive table-wrapper text-center">
          <table className="table table-bordered table-hover border-dark">
            <thead className="text-center tb_header border-dark">
              <tr>
                <th>Recruiter ID</th>
                <th>JDs Posted</th>
                <th>Profiles Submitted</th>
                <th>Total Closures</th>
                <th>Overall Performance (%)</th>
              </tr>
            </thead>

            <tbody>
              {currentRows.map((r) => (
                <tr key={r.recruiter_id}>
                  <td>{r.recruiter_id}</td>
                  <td>{r.jds_posted}</td>
                  <td>{r.profiles_submitted}</td>
                  <td>{r.total_closures}</td>
                  <td>
                    <strong>{r.score}</strong>
                  </td>
                </tr>
              ))}
            </tbody>
            <tfoot>
              <tr style={{ fontWeight: "bold", backgroundColor: "#f1f1f1" }}>
                <td>Total</td>
                <td>{totals.jds_posted}</td>
                <td>{totals.profiles_submitted}</td>
                <td>{totals.total_closures}</td>
                <td>{totals.score}</td>
              </tr>
            </tfoot>
          </table>
        </div>

        <Pagination
          currentPage={currentPage}
          totalPages={totalPages}
          paginate={(page) => setCurrentPage(page)}
        />
      </div>
    </>
  );
}

export default RecruiterRankingDashboard;
